import { useState } from "react";
import { MODULE_CATALOG, PLAN_CORE_MODS } from "../lib/moduleCatalog";
import type { Plan } from "../lib/adminTypes";
import { Field, Modal } from "./Modal";
import { useAdminData } from "../state/AdminDataContext";
import { mmk } from "../lib/format";

export interface PlanFormValue {
  name: string;
  price: number;
  cyclePrices: Record<string, number>;
  stores: number;
  seats: number;
  blurb: string;
  mods: string[];
}

export function PlanModal({
  plan,
  isCreate,
  onClose,
  onSubmit,
  onArchive,
}: {
  plan: Plan | null;
  isCreate: boolean;
  onClose: () => void;
  onSubmit: (form: PlanFormValue) => void;
  onArchive?: () => void;
}) {
  const [name, setName] = useState(plan?.name ?? "");
  const [price, setPrice] = useState(plan ? String(plan.price) : "");
  // Own prices for the longer cycles, as typed; empty means the cycle discount applies.
  const longCycles = useAdminData().cycles.filter((c) => c.months > 1);
  const [own, setOwn] = useState<Record<string, string>>(() => Object.fromEntries(Object.entries(plan?.cyclePrices ?? {}).map(([k, v]) => [k, String(v)])));
  const [stores, setStores] = useState(plan ? String(plan.stores) : "1");
  const [seats, setSeats] = useState(plan ? String(plan.seats) : "5");
  const [blurb, setBlurb] = useState(plan?.blurb ?? "");
  const [mods, setMods] = useState<string[]>(plan ? [...plan.mods] : [...PLAN_CORE_MODS]);

  const toggleMod = (key: string) => {
    if (PLAN_CORE_MODS.includes(key)) return;
    setMods((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const submit = () => {
    if (isCreate && !name.trim()) return;
    onSubmit({
      name: name.trim(),
      price: Number(price) || 0,
      cyclePrices: Object.fromEntries(longCycles.map((c) => [c.key, Math.round(Number(own[c.key]) || 0)] as const).filter(([, v]) => v > 0)),
      stores: Number(stores) || 1,
      seats: Number(seats) || 1,
      blurb,
      mods,
    });
  };

  return (
    <Modal
      onClose={onClose}
      width={520}
      title={isCreate ? "New plan" : "Edit plan"}
      sub={plan?.name}
      footer={
        <>
          {onArchive && (
            <button type="button" className="o-btn o-btn--danger" onClick={onArchive}>
              <span className="ms" aria-hidden="true">inventory_2</span>
              Archive
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button type="button" className="o-btn o-btn--grey" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="o-btn o-btn--ink" onClick={submit} disabled={isCreate && !name.trim()}>
            <span className="ms" aria-hidden="true">check</span>
            {isCreate ? "Create plan" : "Save plan"}
          </button>
        </>
      }
    >
      <div className="o-fields2">
        <Field label="Plan name">
          <input className="o-input" aria-label="Plan name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Pro" />
        </Field>
        <Field label="Monthly price (MMK)">
          <input className="o-input num" aria-label="Monthly price (MMK)" value={price} onChange={(e) => setPrice(e.target.value)} inputMode="numeric" placeholder="e.g. 150000" />
        </Field>
      </div>
      {longCycles.length > 0 && (
        <>
          <div className="o-fields2">
            {longCycles.map((c) => (
              <Field key={c.key} label={`${c.label} price (MMK)`}>
                <input className="o-input num" aria-label={`${c.label} price (MMK)`} value={own[c.key] ?? ""} onChange={(e) => setOwn((prev) => ({ ...prev, [c.key]: e.target.value.replace(/[^0-9]/g, "") }))} inputMode="numeric" placeholder={Math.round((Number(price) || 0) * c.mult).toLocaleString("en-US")} />
              </Field>
            ))}
          </div>
          <div className="o-row__sub" style={{ whiteSpace: "normal", margin: "-6px 0 12px" }}>
            Leave one empty to use the cycle discount: {longCycles.map((c) => `${c.label.toLowerCase()} would be ${mmk(Math.round((Number(price) || 0) * c.mult))}`).join(", ")}.
          </div>
        </>
      )}
      <Field label="Tagline">
        <input className="o-input" aria-label="Tagline" value={blurb} onChange={(e) => setBlurb(e.target.value)} placeholder="Short description" />
      </Field>
      <div className="o-fields2">
        <Field label="Stores (999 = unlimited)">
          <input className="o-input num" aria-label="Stores" value={stores} onChange={(e) => setStores(e.target.value)} inputMode="numeric" placeholder="1" />
        </Field>
        <Field label="Staff seats (999 = unlimited)">
          <input className="o-input num" aria-label="Staff seats" value={seats} onChange={(e) => setSeats(e.target.value)} inputMode="numeric" placeholder="5" />
        </Field>
      </div>
      <Field label={`Included modules, ${mods.length}`}>
        <div className="o-chips" role="group" aria-label="Included modules" style={{ flexWrap: "wrap" }}>
          {MODULE_CATALOG.map((m) => {
            const isCore = PLAN_CORE_MODS.includes(m.key);
            return (
              <button type="button" role="checkbox" aria-checked={isCore || mods.includes(m.key)} aria-disabled={isCore} title={isCore ? "Always included" : undefined} key={m.key} onClick={() => toggleMod(m.key)} style={isCore ? { cursor: "default" } : undefined}>
                {m.name}
              </button>
            );
          })}
        </div>
      </Field>
      <div className="o-note o-note--info" style={{ marginTop: 0 }}>
        <span className="ms" aria-hidden="true">info</span>
        <span>Price changes apply at each business's next renewal.</span>
      </div>
    </Modal>
  );
}
